import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Sparkles, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function CreateJob() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", description: "", num_questions: 5 });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/jobs", {
        title: form.title,
        description: form.description,
        num_questions: Number(form.num_questions),
      });
      toast.success("Role created — questions generated");
      navigate(`/jobs/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create role");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main
      data-testid="create-job-page"
      className="mx-auto max-w-3xl px-6 py-12"
    >
      <button
        data-testid="create-job-back-btn"
        onClick={() => navigate(-1)}
        className="mb-8 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-3 w-3" /> Back
      </button>

      <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700">
        <Sparkles className="mr-1 inline h-3 w-3" /> Add New Job Title
      </div>
      <h1 className="mt-3 font-heading text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
        Tell us about the Job Title
      </h1>
      <p className="mt-4 max-w-xl text-base text-slate-900">
        Add a title (and optional description). We'll generate a tailored,
        camera-ready interview in seconds.
      </p>

      <form onSubmit={onSubmit} className="mt-12 space-y-6 border border-slate-200 p-8">
        <div>
          <Label htmlFor="title" className="text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
            Job title
          </Label>
          <Input
            id="title"
            data-testid="create-job-title-input"
            required
            placeholder="e.g. Senior Backend Engineer"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="mt-2 rounded-sm border-slate-300"
          />
        </div>

        <div>
          <Label htmlFor="desc" className="text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
            Description (optional)
          </Label>
          <Textarea
            id="desc"
            data-testid="create-job-description-input"
            rows={5}
            placeholder="Paste a job description, responsibilities, or required skills…"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="mt-2 rounded-sm border-slate-300"
          />
        </div>

        <div>
          <Label className="text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
            Number of questions
          </Label>
          <Select
            value={String(form.num_questions)}
            onValueChange={(v) => setForm({ ...form, num_questions: Number(v) })}
          >
            <SelectTrigger
              data-testid="create-job-num-questions-select"
              className="mt-2 rounded-sm border-slate-300"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[3, 4, 5, 6, 7, 8].map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n} questions
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 pt-6">
          <div className="text-xs text-slate-500">
            Questions are generated using GPT-5.2.
          </div>
          <Button
            type="submit"
            data-testid="create-job-submit-btn"
            disabled={loading}
            className="bg-brand-gradient rounded-sm text-white"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {loading ? "Generating…" : "Generate interview"}
          </Button>
        </div>
      </form>
    </main>
  );
}
